import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEO from "components/SEO/SEO";
import Slider from "react-slick";
import "./Home.css";

const STATS = [
  { num: "12+", label: "Years Experience", desc: "Since 2012" },
  { num: "500+", label: "Clients Served", desc: "Banks · NBFCs · Fintechs" },
  { num: "95%", label: "Recovery Rate", desc: "Proven track record" },
  { num: "60%", label: "Cost Reduction", desc: "vs in-house operations" },
];

const SERVICES = [
  { icon: "🏦", title: "Field Collections", desc: "Professional doorstep recovery across all Karnataka districts with trained, police-verified executives." },
  { icon: "🔍", title: "Skip Tracing", desc: "Advanced investigative techniques to locate and re-engage hard-to-reach defaulters efficiently." },
  { icon: "📞", title: "BPO Services", desc: "Inbound & outbound call center, customer support, and back-office operations for global clients." },
  { icon: "🤝", title: "Skilled Negotiation", desc: "Customer-centric recovery strategies that preserve relationships while achieving full settlements." },
  { icon: "📊", title: "KPO & Analytics", desc: "Data-driven insights, financial processing, compliance reporting and customer intelligence." },
  { icon: "👥", title: "HR Outsourcing", desc: "End-to-end HR lifecycle — recruitment, payroll, training and statutory compliance management." },
];

const PARTNERS = ["Kotak Mahindra Bank","IDFC First Bank","RBL Bank","Yes Bank","Bandhan Bank",
  "IIFL Finance","Hero Fincorp","L&T Finance","Kinara Capital","Piramal Finance",
  "One Card","KreditBee","Slice","MobiKwik","MoneyView","Axio","IARC","TVS Credit"];

const SLIDER_SETTINGS = {
  dots: true, infinite: true, slidesToShow: 1, slidesToScroll: 1,
  arrows: false, autoplay: true, autoplaySpeed: 4000, pauseOnHover: false,
  fade: true, cssEase: "ease-in-out",
};

export default function Home() {
  const counterRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("counted");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    counterRef.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="CSR Associates | Debt Recovery & BPO Services Bangalore India"
        description="CSR Associates — Bangalore's leading debt recovery and BPO company since 2012. Trusted by Kotak, IDFC, RBL Bank. Ethical collections, field recovery, skip tracing. 95% recovery rate."
        keywords="debt recovery bangalore, BPO services karnataka, collections agency india, NBFC debt collection, field collections bangalore, loan recovery agency"
      />
      <div className="home-page">

        {/* ── HERO ── */}
        <section className="hero-section" aria-label="Hero">
          <div className="hero-bg-grid" aria-hidden="true" />
          <div className="hero-bg-glow" aria-hidden="true" />
          <div className="hero-inner container">
            <div className="hero-content" data-aos="fade-up">
              <div className="hero-kicker">
                <span className="hero-kicker-line" />
                <span>Est. 2012 · Bangalore, Karnataka · India</span>
              </div>
              <h1 className="hero-title">
                Trusted <em>Debt Recovery</em><br />
                & BPO Services<br />
                <span className="hero-title-sub">Across Karnataka & Beyond</span>
              </h1>
              <p className="hero-desc">
                CSR Associates delivers ethical, compliant, and results-driven debt collection
                and business process outsourcing for India's leading banks, NBFCs, and fintechs.
                12+ years of proven performance. 500+ satisfied clients.
              </p>
              <div className="hero-actions">
                <Link to="/contact" className="btn-gold">Partner With Us</Link>
                <Link to="/channel-partners" className="btn-outline-gold">Our Partners</Link>
              </div>
            </div>

            <div className="hero-slider-wrap" data-aos="fade-left" data-aos-delay="200">
              <Slider {...SLIDER_SETTINGS}>
                {[
                  { src: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=900&q=80", alt: "CSR Associates office team" },
                  { src: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=900&q=80", alt: "Professional debt recovery operations" },
                  { src: "https://images.unsplash.com/photo-1560472355-536de3962603?w=900&q=80", alt: "BPO services Bangalore" },
                ].map((img, i) => (
                  <div key={i} className="hero-slide">
                    <img src={img.src} alt={img.alt} loading={i === 0 ? "eager" : "lazy"} />
                    <div className="hero-slide-overlay" />
                  </div>
                ))}
              </Slider>
            </div>
          </div>

          {/* Stats bar */}
          <div className="hero-stats-bar" data-aos="fade-up" data-aos-delay="300">
            <div className="container">
              <div className="hero-stats-grid">
                {STATS.map((s, i) => (
                  <div key={i} className="hero-stat" ref={el => counterRef.current[i] = el}>
                    <span className="hero-stat-num">{s.num}</span>
                    <span className="hero-stat-label">{s.label}</span>
                    <span className="hero-stat-desc">{s.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── ABOUT STRIP ── */}
        <section className="about-strip" data-aos="fade-up">
          <div className="container about-strip-inner">
            <div>
              <div className="eyebrow">Who We Are</div>
              <h2 className="section-heading">
                Bangalore's Premier <span className="accent">Debt Recovery</span> Partner
              </h2>
              <p className="section-lead" style={{ marginBottom: "28px" }}>
                Founded in 2012, CSR Associates has grown from a 2-person team to Karnataka's most
                trusted collections and outsourcing firm. Our success is built on a single principle:
                <strong style={{ color: "var(--gold-lt)" }}> ethical recovery that protects every stakeholder.</strong>
              </p>
              <p className="section-lead">
                We serve MNC banks, NBFCs, and fintechs with a comprehensive suite of services including
                field collections, skip tracing, telecalling, BPO operations, and HR outsourcing — all
                with full RBI Fair Practices Code compliance.
              </p>
              <div className="about-strip-actions">
                <Link to="/about" className="btn-gold">Our Story</Link>
                <Link to="/awards-and-recognitions" className="btn-outline-gold">Awards</Link>
              </div>
            </div>
            <div className="about-strip-right">
              <div className="mission-card glass-card" data-aos="fade-left" data-aos-delay="100">
                <div className="mc-icon">🎯</div>
                <h3>Our Mission</h3>
                <p>Provide professional, compassionate debt recovery that resolves financial obligations
                  efficiently while maintaining dignity and respect for all parties involved.</p>
              </div>
              <div className="mission-card glass-card" data-aos="fade-left" data-aos-delay="200">
                <div className="mc-icon">🌐</div>
                <h3>Our Vision</h3>
                <p>Be India's most trusted outsourcing partner — recognised for results, integrity,
                  and an unwavering commitment to our clients' financial health.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SERVICES ── */}
        <section className="services-section" id="services" data-aos="fade-up">
          <div className="container">
            <div className="services-header">
              <div className="eyebrow">What We Do</div>
              <h2 className="section-heading">
                Comprehensive <span className="accent">Outsourcing Solutions</span>
              </h2>
              <p className="section-lead">
                From doorstep debt collection to global BPO — every service we offer is
                engineered for measurable outcomes and sustainable growth.
              </p>
            </div>
            <div className="services-grid">
              {SERVICES.map((svc, i) => (
                <div key={i} className="service-card glass-card" data-aos="fade-up" data-aos-delay={i * 60}>
                  <div className="svc-icon">{svc.icon}</div>
                  <h3>{svc.title}</h3>
                  <p>{svc.desc}</p>
                  <Link to="/contact" className="svc-link">Enquire →</Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY US ── */}
        <section className="why-section" data-aos="fade-up">
          <div className="container why-inner">
            <div>
              <div className="eyebrow">Why Choose CSR</div>
              <h2 className="section-heading">
                What Sets Us <span className="accent">Apart</span>
              </h2>
              <div className="why-points">
                {[
                  ["🏆", "12+ Years of Proven Performance", "Consistent results across 500+ banking and fintech clients since 2012."],
                  ["⚖️", "RBI-Compliant Ethical Collections", "Full Fair Practices Code adherence protecting your brand and customers."],
                  ["📍", "Karnataka-Wide Field Network", "Deep hyper-local presence covering every district and pin code in Karnataka."],
                  ["🤖", "Technology-Driven Operations", "AI-powered analytics, skip tracing tools, and modern CRM for faster outcomes."],
                  ["📈", "95% Recovery Rate", "India-leading collection success rate across personal loans, credit cards and NBFC portfolios."],
                ].map(([icon, title, desc], i) => (
                  <div key={i} className="why-point" data-aos="fade-right" data-aos-delay={i * 80}>
                    <span className="why-icon">{icon}</span>
                    <div>
                      <strong>{title}</strong>
                      <p>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="why-image-wrap" data-aos="fade-left">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=700&q=80"
                alt="CSR Associates professional team Bangalore"
                loading="lazy"
              />
              <div className="why-badge">
                <span className="why-badge-num">12+</span>
                <span className="why-badge-label">Years of Excellence</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── PARTNER MARQUEE ── */}
        <section className="partners-strip" data-aos="fade-up">
          <div className="container">
            <div className="eyebrow" style={{ justifyContent: "center" }}>Trusted By</div>
            <h2 className="section-heading" style={{ textAlign: "center", marginBottom: "40px" }}>
              India's Leading <span className="accent">Financial Institutions</span>
            </h2>
          </div>
          <div className="marquee-wrap">
            <div className="marquee-track">
              {[...PARTNERS, ...PARTNERS].map((p, i) => (
                <div key={i} className="marquee-item">
                  <span className="marquee-dot" />
                  {p}
                </div>
              ))}
            </div>
          </div>
          <div className="container" style={{ textAlign: "center", marginTop: "32px" }}>
            <Link to="/channel-partners" className="btn-outline-gold">View All Partners</Link>
          </div>
        </section>

        {/* ── CTA BANNER ── */}
        <section className="cta-section" data-aos="fade-up">
          <div className="container cta-inner">
            <div>
              <div className="eyebrow">Partner With Us</div>
              <h2 className="section-heading">
                Ready to <span className="accent">Transform</span> Your Operations?
              </h2>
              <p className="section-lead">
                Join 500+ businesses that trust CSR Associates for scalable, cost-effective
                debt recovery and outsourcing solutions.
              </p>
            </div>
            <div className="cta-actions">
              <Link to="/contact" className="btn-gold">Get in Touch</Link>
              <a
                href="https://wa.me/919008328891?text=Hello%2C%20I%27d%20like%20to%20know%20more%20about%20CSR%20Associates%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-gold"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
